import { environment, firebaseApp } from "$lib/utils/env";
import { getAuth, onAuthStateChanged, type UserInfo } from "firebase/auth";
import { derived, readable, type Readable, type Unsubscriber } from "svelte/store";
import { chainStores, getDocumentAsStore } from "$lib/utils/firestore";
import Clarity from "@microsoft/clarity";
import { makeApiRequest } from "$lib/utils/api";

export interface AuthState {
  loading: boolean;
  userInfo?: UserInfo & {
    providerData: [
      {
        providerId: string;
      },
    ];
  };
}

export const authStateStore = readable<AuthState>({ loading: true }, (set) => {
  let unsubAuth: Unsubscriber;
  firebaseApp.then((firebaseApp) => {
    const auth = getAuth(firebaseApp);

    unsubAuth = onAuthStateChanged(auth, (userInfo: UserInfo | null) => {
      set({
        loading: false,
        userInfo: userInfo ?? undefined,
      });

      if (!!userInfo && auth.currentUser?.metadata.creationTime == auth.currentUser?.metadata.lastSignInTime) {
        // creationTime == lastSignInTime implies this is the first login of a new user
        // call backend refresh-claims endpoint
        // this is an expensive endpoint, we don't want to call it very often
        makeApiRequest("/me/refresh-claims", {}, "POST");
      }
    });
  });
  return () => {
    unsubAuth();
  };
});

let authStateLoadedUnsub: Unsubscriber;
export const authLoaded = new Promise<void>((resolve, reject) => {
  authStateLoadedUnsub = authStateStore.subscribe((authState) => {
    if (!authState.loading) {
      resolve();
    }
  });
}).then(() => {
  authStateLoadedUnsub();
});

const rawUserStore = chainStores(authStateStore, undefined, (authState) => {
  if (authState.loading || !authState.userInfo) {
    return readable(undefined);
  }
  return getDocumentAsStore({
    docPath: `users/${authState.userInfo.uid}`,
    extraData: { userInfo: authState.userInfo },
  });
});

rawUserStore.subscribe((r) => console.log("Raw User store changed", r));

export async function getUserIdToken() {
  return await getAuth(await firebaseApp).currentUser?.getIdToken();
}

export interface User {
  email: string;
  name: string;
  defaultTeam: string;
  teamClaims: {
    teamId: string;
    claims: string[];
  }[];
  orgClaims: {
    orgId: string;
    claims: string[];
  }[];
  organisation_setup_id: string;
}

export const userStore: Readable<User | undefined> = derived(rawUserStore, (rawUser) => {
  if (rawUser) {
    Clarity.consent(rawUser.data.cookie_consent ?? true);

    environment.then(({ adminEmails = [] }) => {
      if (!adminEmails.some((domain) => rawUser.userInfo.email?.endsWith(`@${domain}`))) {
        Clarity.upgrade("Upgrading all non-redactive sessions");
      }
    });

    const teamClaims: User["teamClaims"] = [];
    Object.entries(rawUser.data.team_claims ?? {}).forEach(([teamId, claims]) => {
      teamClaims.push({
        teamId: teamId,
        claims: claims as string[],
      });
    });
    const orgClaims: User["orgClaims"] = [];
    Object.entries(rawUser.data.organisation_claims ?? {}).forEach(([orgId, claims]) => {
      orgClaims.push({
        orgId: orgId,
        claims: claims as string[],
      });
    });
    const email = rawUser.data.preferences?.primary_email || rawUser.userInfo?.email;
    return {
      email: email,
      name: rawUser.data.preferences?.name || email.split("@")[0],
      defaultTeam: rawUser.data.preferences?.default_team,
      teamClaims: teamClaims,
      orgClaims: orgClaims,
      organisation_setup_id: rawUser.data.organisation_setup_id,
    };
  } else {
    return undefined;
  }
});

userStore.subscribe((u) => console.log("User store changed", u));
